"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { cn } from "@/lib/utils";
import { geocodePostalCode, type GeocodeResult } from "@/lib/geocoding/geocode";
import { normalizeZipInput } from "@/lib/validation/core/primitives";
import { ZipStatusIndicator } from "./zip-status-indicator";
import { useDepsChanged } from "@/hooks/use-synced-state";

export interface SimpleZipInputProps {
  value: string;
  onChange: (zip: string) => void;
  onZipSelect?: (data: GeocodeResult, postalCode: string) => void;
  placeholder?: string;
  className?: string;
  showValidIndicator?: boolean;
  disabled?: boolean;
}

type LookupState = "idle" | "loading" | "valid" | "invalid";

const ZIP_FORMAT_REGEX = /^\d{5}$/;

function useZipLookup(
  onZipSelect: SimpleZipInputProps["onZipSelect"],
) {
  const [lookupState, setLookupState] = useState<LookupState>("idle");
  const [displayInfo, setDisplayInfo] = useState<string | null>(null);
  const lookupRequestRef = useRef(0);

  const performLookup = useCallback(
    async (zip: string) => {
      if (!ZIP_FORMAT_REGEX.test(zip)) {
        setLookupState("idle");
        setDisplayInfo(null);
        return;
      }
      const requestId = lookupRequestRef.current + 1;
      lookupRequestRef.current = requestId;
      setLookupState("loading");
      const result = await geocodePostalCode(zip);
      if (lookupRequestRef.current !== requestId) {
        return;
      }
      if (result) {
        setLookupState("valid");
        setDisplayInfo(`${result.city}, ${result.state}`);
        onZipSelect?.(result, zip);
      } else {
        setLookupState("invalid");
        setDisplayInfo(null);
      }
    },
    [onZipSelect],
  );

  const cancelLookup = useCallback(() => {
    lookupRequestRef.current += 1;
    setLookupState("idle");
    setDisplayInfo(null);
  }, []);

  return { lookupState, displayInfo, performLookup, cancelLookup };
}

function usePendingTimeout() {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearPending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const schedule = useCallback((callback: () => void, delayMs: number) => {
    timeoutRef.current = setTimeout(callback, delayMs);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return { clearPending, schedule };
}

function useZipInput(
  value: string,
  onChange: (zip: string) => void,
  onZipSelect: SimpleZipInputProps["onZipSelect"],
) {
  const [inputValue, setInputValue] = useState(value);
  const { lookupState, displayInfo, performLookup, cancelLookup } =
    useZipLookup(onZipSelect);
  const { clearPending, schedule } = usePendingTimeout();

  if (useDepsChanged([value])) {
    setInputValue(value);
  }

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = normalizeZipInput(e.target.value);
      cancelLookup();
      setInputValue(newValue);
      onChange(newValue);
      clearPending();
      if (newValue.length === 5) {
        schedule(() => {
          void performLookup(newValue);
        }, 300);
      }
    },
    [onChange, performLookup, cancelLookup, clearPending, schedule],
  );

  const handleBlur = useCallback(() => {
    if (inputValue.length === 5 && lookupState === "idle") {
      clearPending();
      performLookup(inputValue);
    }
  }, [inputValue, lookupState, performLookup, clearPending]);

  return { inputValue, lookupState, displayInfo, handleChange, handleBlur };
}

export function SimpleZipInput({
  value,
  onChange,
  onZipSelect,
  placeholder = "ZIP code",
  className,
  showValidIndicator = true,
  disabled = false,
}: SimpleZipInputProps) {
  const { inputValue, lookupState, displayInfo, handleChange, handleBlur } =
    useZipInput(value, onChange, onZipSelect);
  const hasIndicator = showValidIndicator && lookupState !== "idle";

  return (
    <div className="relative">
      <input
        type="text"
        inputMode="numeric"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          "w-full transition-colors",
          hasIndicator && "pr-8",
          className,
        )}
      />
      {showValidIndicator && (
        <ZipStatusIndicator
          lookupState={lookupState}
          displayInfo={displayInfo}
        />
      )}
    </div>
  );
}
